import { useMemo, type ReactNode } from "react";
import dayjs from "dayjs";
import { ExternalLink } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { formatDateTime } from "@marekl/adminer/format";
import {
  Badge,
  EmptyState,
  FilterField,
  FilterPanel,
  Input,
  Page,
  Pagination,
  Select,
  Table,
  Tabs,
  Text,
  type Column,
} from "@marekl/adminer/components";
import {
  useConfirm,
  useDebouncedSearchFilter,
  useOffsetPaginatedQuery,
} from "@marekl/adminer/hooks";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  useCancelMail,
  useMailOutbox,
  useSendMailNow,
  type MailOutboxRow,
  type MailOutboxStatus,
} from "../../../lib/queries/mailOutbox";
import {
  MAIL_KIND_LABELS,
  MAIL_STATUS_LABELS,
  MAIL_STATUS_VARIANT,
  mailKindLabel,
} from "./mailLabels";
import { MailSettingsTab } from "./MailSettingsTab";
import { readsOnlyAsGlobalViewer, useMe } from "../../../auth/queries";

const TITLE = "E-maily";
const SUBTITLE = "co systém posílá, co čeká a co odešlo";

const TABS = [
  { id: "outbox", label: "Přehled" },
  { id: "settings", label: "Nastavení" },
];

const LIMIT_OPTIONS = [25, 50, 100, 200];

/**
 * When a waiting mail leaves. A time still ahead is its window; one that
 * has passed means it is due now, or, after a failed attempt, waiting for
 * the worker's next retry, whose time only the worker knows.
 */
function pendingNote(r: MailOutboxRow) {
  if (r.not_before.isAfter(dayjs())) {
    return <Text variant="muted">odejde {formatDateTime(r.not_before)}</Text>;
  }
  if (r.attempts > 0) {
    return <Text variant="muted">čeká na další pokus</Text>;
  }
  return <Text variant="muted">odchází</Text>;
}
const DEFAULT_LIMIT = 50;

/**
 * /admin/maily (docs/maily.md R13): what cmfis owes, sends and has sent.
 * Delivery itself is letterman's; a sent row links to it there.
 *
 * Not sortable: it reads newest first, and the filters are the way to find
 * a row. A sortable column would also owe a case in
 * e2e/specs/razeni/sorting.spec.ts for no question anybody asks.
 */
export function MailOutboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "settings" ? "settings" : "outbox";
  const tabs = (
    <Tabs
      tabs={TABS}
      value={tab}
      // The outbox filters mean nothing on the other tab, so a switch drops them.
      onChange={(next) => setSearchParams(next === "outbox" ? {} : { tab: next }, { replace: true })}
      aria-label="Části stránky E-maily"
    />
  );

  return (
    <>
      <AppTopbar crumbs={[{ label: "E-maily" }]} />
      {tab === "settings" ? (
        <Page>
          <Page.Header title={TITLE} subtitle={SUBTITLE} />
          <Page.Content stack>
            {tabs}
            <MailSettingsTab />
          </Page.Content>
        </Page>
      ) : (
        <OutboxTab tabs={tabs} />
      )}
    </>
  );
}

/** The outbox list. A whole Page, so its pagination can sit in Page.Footer. */
function OutboxTab({ tabs }: { tabs: ReactNode }) {
  const { data: me } = useMe();
  // Čtení všeho reads the outbox; sending or cancelling a mail is admin's.
  const readOnly = readsOnlyAsGlobalViewer(me);
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  const sendNow = useSendMailNow();
  const cancel = useCancelMail();

  const q = searchParams.get("q") ?? "";
  const status = searchParams.get("status") ?? "";
  const kind = searchParams.get("kind") ?? "";

  const { data, isLoading, error, refetch, pagination, setFilter } = useOffsetPaginatedQuery(
    useMailOutbox,
    { q: q || undefined, status: status || undefined, kind: kind || undefined },
    { searchParams, setSearchParams, defaultLimit: DEFAULT_LIMIT, limitOptions: LIMIT_OPTIONS },
  );

  const { draft: qDraft, setDraft: setQDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => setFilter("q", next || null),
  });

  const columns = useMemo<Column<MailOutboxRow>[]>(
    () => [
      {
        key: "created",
        header: "Vytvořeno",
        render: (r) => <Text variant="mono">{formatDateTime(r.created_at)}</Text>,
      },
      {
        key: "kind",
        header: "E-mail",
        render: (r) => mailKindLabel(r.kind),
      },
      {
        key: "to",
        header: "Příjemce",
        // Two lines: who, then where. A recipient with no account has
        // neither, and the row says so once.
        render: (r) =>
          r.address ? (
            <>
              <div>{r.name || r.address}</div>
              {r.name && <Text variant="muted">{r.address}</Text>}
            </>
          ) : (
            <Text variant="muted">nemá účet</Text>
          ),
      },
      {
        key: "entity",
        header: "Týká se",
        // A set-password mail is about an account and keeps no reference to it.
        render: (r) =>
          r.entity_type === "user" ? <Text>Účet</Text> : <Text variant="mono">{r.entity_ref || r.entity_type}</Text>,
      },
      {
        key: "status",
        header: "Stav",
        // Two lines: the state, then when it happened or will.
        render: (r) => (
          <>
            <div>
              <Badge variant={MAIL_STATUS_VARIANT[r.status] ?? "muted"}>
                {r.in_flight ? "Odesílá se" : (MAIL_STATUS_LABELS[r.status] ?? r.status)}
              </Badge>
            </div>
            {r.status === "pending" && !r.in_flight && pendingNote(r)}
            {r.status === "sent" && r.sent_at && <Text variant="muted">{formatDateTime(r.sent_at)}</Text>}
          </>
        ),
      },
      {
        key: "attempts",
        header: "Pokusy",
        render: (r) => <Text variant="number">{r.attempts}</Text>,
      },
      {
        key: "error",
        header: "Poslední chyba",
        render: (r) =>
          r.last_error ? (
            <Text variant={r.config_error ? undefined : "muted"}>
              {r.config_error ? "Chybné nastavení odesílání e-mailů: " : ""}
              {r.last_error}
            </Text>
          ) : (
            <Text variant="muted">—</Text>
          ),
      },
      {
        key: "actions",
        header: "",
        render: (r) => (
          <Table.Actions>
            {!readOnly && r.status === "pending" && !r.in_flight && (
              <>
                <Table.Action
                  appearance="button"
                  onClick={() =>
                    confirm({
                      title: "Odeslat e-mail hned",
                      description: (
                        <>
                          E-mail <strong>{mailKindLabel(r.kind)}</strong> pro {r.address || "příjemce bez účtu"}{" "}
                          přestane čekat a odejde při příštím průchodu, zpravidla do minuty. Pokud se jeho
                          odeslání už jednou nepovedlo, odejde až při dalším pokusu.
                        </>
                      ),
                      confirmLabel: "Odeslat hned",
                      onConfirm: () => sendNow.mutateAsync(r.id),
                    })
                  }
                >
                  Odeslat hned
                </Table.Action>
                <Table.Action
                  appearance="button"
                  variant="danger"
                  onClick={() =>
                    confirm({
                      title: "Zrušit e-mail",
                      description: (
                        <>
                          E-mail <strong>{mailKindLabel(r.kind)}</strong> pro {r.address || "příjemce bez účtu"}{" "}
                          neodejde. Zrušení nejde vrátit.
                        </>
                      ),
                      confirmLabel: "Zrušit e-mail",
                      variant: "danger",
                      onConfirm: () => cancel.mutateAsync(r.id),
                    })
                  }
                >
                  Zrušit
                </Table.Action>
              </>
            )}
            {r.admin_url && (
              // letterman's admin, in a new tab: `to` would route it through
              // the app's router, which cannot open another tab.
              <Table.Action
                appearance="button"
                endIcon={<ExternalLink />}
                onClick={() => window.open(r.admin_url, "_blank", "noopener,noreferrer")}
              >
                Doručení
              </Table.Action>
            )}
          </Table.Actions>
        ),
      },
    ],
    [confirm, sendNow, cancel, readOnly],
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const statuses = Object.keys(MAIL_STATUS_LABELS) as MailOutboxStatus[];

  return (
    <Page>
      <Page.Header title={TITLE} subtitle={SUBTITLE} />
      <Page.Content>
        {tabs}
        <FilterPanel>
          <FilterField wide>
            <Input
              label="Hledat"
              type="search"
              value={qDraft}
              placeholder="adresa, jméno nebo číslo přihlášky"
              onChange={(e) => setQDraft(e.target.value)}
            />
          </FilterField>
          <FilterField>
            <Select label="Stav" value={status} onChange={(e) => setFilter("status", e.target.value || null)}>
              <option value="">Všechny</option>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {MAIL_STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          </FilterField>
          <FilterField>
            <Select label="E-mail" value={kind} onChange={(e) => setFilter("kind", e.target.value || null)}>
              <option value="">Všechny</option>
              {Object.entries(MAIL_KIND_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </Select>
          </FilterField>
        </FilterPanel>

        <Page.SectionHeader title="E-maily" meta={total > 0 ? `${items.length} z ${total}` : undefined} />

        <Table
          framed
          columns={columns}
          rows={items}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          empty={<EmptyState title="Žádné e-maily" description="Pro zadané filtry žádný e-mail neexistuje." />}
        />
      </Page.Content>
      <Page.Footer>
        <Pagination {...pagination} />
      </Page.Footer>
    </Page>
  );
}
